import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import { readContract } from '@wagmi/core';
import parameterGroupRegistryABI from '../contracts/tgetherParameterGroupsRegistry.json';
import ParameterGroupCard from './Items/ParameterGroupCard.tsx';
import * as config from '../config.tsx';
import RegisterParameterGroupModal from './FormModals/RegisterParameterGroupModal.tsx';

const parameterGroupRegistryAddress = process.env.REACT_APP_PARAMETER_GROUP_REGISTRY_ADDRESS as `0x${string}`;

interface ParameterGroupsProps {
    value: string;
}

const ParameterGroups: React.FC<ParameterGroupsProps> = ({ value }) => {
    const [parameterGroups, setParameterGroups] = useState<any[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pageNum, setPageNum] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        if (value) {
            fetchParameterGroupByName(value);
        } else {
            fetchParameterGroups(0);
        }
    }, [value]);

    const fetchParameterGroups = async (page: number) => {
        setLoading(true);
        setError('');
        try {
            const parameterGroupNames = await readContract(config.config, {
                abi: parameterGroupRegistryABI.abi,
                address: parameterGroupRegistryAddress,
                functionName: 'getParameterGroupNamesPaginated',
                args: [10, page], // Fetching 10 parameter groups at a time
            }) as string[];

            const groups = await Promise.all(
                parameterGroupNames.map(async (name) => {
                    const group = await readContract(config.config, {
                        abi: parameterGroupRegistryABI.abi,
                        address: parameterGroupRegistryAddress,
                        functionName: 'parameterGroupsByName',
                        args: [name],
                    });
                    return group;
                })
            );
            console.log('Group:', groups);


            setParameterGroups(groups);
            setPageNum(page);
            setHasMore(parameterGroupNames.length === 10); // Check if we have exactly 10 items
        } catch (err) {
            console.error('Error fetching parameter groups:', err);
            setError('Failed to fetch parameter groups.');
        } finally {
            setLoading(false);
        }
    };
    const handleEnrollClick = (groupName: string) => {
        // Handle the logic to enroll the parameter group in a community here
        console.log(`Enrolling group ${groupName} in a community`);
        // You could show a modal to let the user select the community, or trigger any other action
    };
    

    const fetchParameterGroupByName = async (name: string) => {
        setLoading(true);
        setError('');
        try {
            const group = await readContract(config.config, {
                abi: parameterGroupRegistryABI.abi,
                address: parameterGroupRegistryAddress,
                functionName: 'parameterGroupsByName',
                args: [name],
            });


            if (!group || group[0] === '') {
                setError('Parameter group does not exist.');
                setParameterGroups([]);
            } else {
                setParameterGroups([group]);
            }
        } catch (err) {
            console.error('Error fetching parameter group:', err);
            setError('Failed to fetch parameter group.');
        } finally {
            setLoading(false);
        }
    };

    const handleShowRegisterModal = () => setShowRegisterModal(true);
    const handleHideRegisterModal = () => setShowRegisterModal(false);

    const handleNextPage = () => {
        if (hasMore) {
            fetchParameterGroups(pageNum + 1);
        }
    };

    const handlePreviousPage = () => {
        if (pageNum > 0) {
            fetchParameterGroups(pageNum - 1);
        }
    };

    const handleShowAll = () => {
        fetchParameterGroups(0);
    };

    const handleCardClick = (group: any) => {
        setSelectedGroup(group);
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Parameter Groups</h4>
                <div>
                    <Button variant="secondary" onClick={handleShowRegisterModal}>
                        Register New Parameter Group
                    </Button>
                </div>
            </div>
            {loading && <Alert variant="info">Loading parameter groups...</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {parameterGroups.length === 0 && value && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Button variant="warning" onClick={handleShowAll}>
                        Show All
                    </Button>
                </div>
            )}
            <Row>
                {parameterGroups.map((group, index) => (
                    <Col key={index} sm={12} md={6} lg={4} className="mb-4">
                    <ParameterGroupCard
                        group={group}
                        onClick={() => handleCardClick(group)}
                        isSelected={selectedGroup === group} // Pass selected state
                        onEnrollClick={handleEnrollClick} // Provide the onEnrollClick function
                    />

                    </Col>
                ))}
            </Row>
            <div className="d-flex justify-content-between mt-3">
                <Button variant="outline-secondary" onClick={handlePreviousPage} disabled={pageNum === 0}>
                    Back
                </Button>
                <Button variant="outline-secondary" onClick={handleNextPage} disabled={!hasMore}>
                    Next
                </Button>
            </div>
            <RegisterParameterGroupModal
                show={showRegisterModal}
                onHide={handleHideRegisterModal}
            />

        </div>
    );
};

export default ParameterGroups;
